import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Save, Search, ChevronRight, ChevronDown, Pencil, X, Package, Link2, Loader2, TrendingUp, Download } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const split = (s) => (s || "").split(/[,\n]/).map((x) => x.trim()).filter(Boolean);
const BR = "DTF, Haft, Silikon, Flock, Same metki";
const PL = "przód, tył, lewy rękaw, prawy rękaw";

// Stała lista kolorów bazowych. Kolejność = priorytet dopasowania (np. granat przed niebieskim).
const COLOR_GROUPS = [
  ["Czarny", ["black", "czarn", "noir", "schwarz", "onyx", "jet"]],
  ["Biały", ["white", "bial", "biał", "blanc", "weiss"]],
  ["Granatowy", ["navy", "granat", "marine", "marino", "midnight"]],
  ["Niebieski", ["blue", "niebiesk", "royal", "sky", "azure", "cobalt", "denim"]],
  ["Turkusowy", ["turquoise", "turkus", "teal", "aqua", "cyan"]],
  ["Bordowy", ["maroon", "burgund", "bordo", "wine", "claret"]],
  ["Czerwony", ["red", "czerwon", "fire", "cardinal", "cherry", "scarlet"]],
  ["Pomarańczowy", ["orange", "pomarań", "pomaran", "apricot", "coral"]],
  ["Żółty", ["yellow", "żółt", "zolt", "gold", "mustard", "amber"]],
  ["Zielony", ["green", "ziel", "olive", "khaki", "bottle", "kelly", "forest", "lime", "mint", "moss", "emerald"]],
  ["Fioletowy", ["purple", "fiolet", "violet", "lavender", "plum", "mauve"]],
  ["Różowy", ["pink", "róż", "roz", "fuchsia", "fuksja", "magenta"]],
  ["Szary", ["grey", "gray", "szar", "graphite", "grafit", "charcoal", "anthracite", "antracyt", "silver", "srebr", "heather", "melange", "melanż", "melanz", "marl"]],
  ["Brązowy", ["brown", "brąz", "braz", "chocolate", "coffee", "hazel"]],
  ["Beżowy", ["beige", "beż", "sand", "cream", "krem", "ecru", "ivory", "natural", "nude", "stone"]],
];
const baseColorOf = (raw) => {
  const s = (raw || "").toLowerCase();
  for (const [label, kws] of COLOR_GROUPS) if (kws.some((k) => s.includes(k))) return label;
  return "Inne";
};

const isIndivBelow = (below) => below === "individual" || below == null || Number.isNaN(Number(below));
const commissionSummary = (rule) => {
  if (!rule) return null;
  const tiers = [...(rule.tiers || [])].sort((a, b) => (b.min_price || 0) - (a.min_price || 0));
  const parts = tiers.map((t) => `od ${t.min_price} zł → ${t.commission} zł`);
  parts.push(`poniżej: ${isIndivBelow(rule.below) ? "indywidualne" : `${rule.below} zł`}`);
  return parts;
};

export const ProductsManager = ({ onSaved, isAdmin }) => {
  const [items, setItems] = useState([]);
  const [rules, setRules] = useState({});
  const [query, setQuery] = useState("");
  const [colorFilter, setColorFilter] = useState("");
  const [baseColor, setBaseColor] = useState("");
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(new Set());
  const [importOpen, setImportOpen] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [showProfit, setShowProfit] = useState(false);
  const [profit, setProfit] = useState(null);
  const fmt = (n) => (n == null ? "0,00" : Number(n).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  const toggleProfit = async () => {
    const next = !showProfit; setShowProfit(next);
    if (next && !profit) { try { const r = await api.get("/products/profit"); setProfit(r.data); } catch (e) { toast.error(errMsg(e)); } }
  };

  const load = () => api.get("/products")
    .then((r) => setItems(r.data.map((p) => ({ ...p, colorsStr: (p.colors || []).join(", "), sizesStr: (p.sizes || []).join(", "), brandingStr: (p.branding_types || []).join(", "), placementsStr: (p.placements || []).join(", ") }))))
    .catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/catalog").then((r) => setRules(r.data.commission_rules || {})).catch(() => {}); }, []);

  const setField = (id, f, v) => setItems((prev) => prev.map((p) => (p.id === id ? { ...p, [f]: v } : p)));
  const setWapro = (id, size, v) => setItems((prev) => prev.map((p) => (p.id === id ? { ...p, wapro_index: { ...(p.wapro_index || {}), [size]: v } } : p)));
  const [fetching, setFetching] = useState(null);
  const fetchSpecs = async (p) => {
    setFetching(p.id);
    try {
      const { data } = await api.post(`/products/${p.id}/fetch-specs`, { source: (p.source || "").trim() });
      const u = data.updated || {};
      setItems((prev) => prev.map((x) => (x.id === p.id ? { ...x, ...u, source: data.source || x.source } : x)));
      const keys = Object.keys(u);
      toast.success(keys.length ? `Pobrano cechy (${keys.join(", ")}). Kliknij Zapisz.` : "Strona nie zawierała cech do pobrania.");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setFetching(null); }
  };
  const toggle = (k) => setOpen((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const addNew = () => {
    const id = `new-${Date.now()}`;
    setItems((prev) => [{ id, _new: true, catalog_no: "", sku: "", type: "", name: "", brand: "", colorsStr: "", sizesStr: "", brandingStr: BR, placementsStr: PL }, ...prev]);
    setEditId(id);
  };

  const runImport = async () => {
    const urls = importUrl.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
    if (!urls.length) return toast.error("Wklej co najmniej jeden link (każdy w nowej linii)");
    const bad = urls.find((u) => !/^https?:\/\//i.test(u));
    if (bad) return toast.error(`Nieprawidłowy link: ${bad}`);
    setImporting(true);
    try {
      if (urls.length === 1) {
        const { data } = await api.post("/products/import-preview", { url: urls[0] });
        const d = data.draft || {};
        const id = `new-${Date.now()}`;
        setItems((prev) => [{ id, _new: true, catalog_no: d.catalog_no || "", sku: d.sku || "", type: d.type || "", name: d.name || "", brand: d.brand || "", colorsStr: (d.colors || []).join(", "), sizesStr: (d.sizes || []).join(", "), brandingStr: (d.branding_types || []).join(", ") || BR, placementsStr: (d.placements || []).join(", ") || PL, gramatura: d.gramatura || "", sklad: d.sklad || "", kroj: d.kroj || "", dekolt: d.dekolt || "", szwy: d.szwy || "" }, ...prev]);
        setEditId(id);
        toast.success(data.exists ? `Zaimportowano — UWAGA: SKU „${d.sku}" już istnieje. Sprawdź przed zapisem.` : "Zaimportowano dane produktu. Sprawdź i kliknij Zapisz.");
      } else {
        const { data } = await api.post("/products/import-batch", { urls });
        const c = (data.created || []).length, dup = (data.duplicates || []).length, f = (data.failed || []).length;
        await load(); onSaved && onSaved();
        if (c) toast.success(`Dodano ${c} produkt(ów)${dup ? `, pominięto ${dup} istniejących` : ""}${f ? `, ${f} z błędem` : ""}.`);
        else toast.error(dup && !f ? `Nic nie dodano — ${dup} już istnieje w katalogu.` : "Nie zaimportowano żadnego produktu.");
        if (f) toast.error(`Błędy: ${data.failed.map((x) => `${x.error} (${x.url})`).join(" | ")}`, { duration: 8000 });
      }
      setImportOpen(false); setImportUrl("");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setImporting(false); }
  };

  const confirm = useConfirm();
  const save = async (p) => {
    if (!p.sku.trim() || !p.name.trim()) return toast.error("Podaj SKU i nazwę");
    if (!(await confirm({ title: p._new ? "Dodać produkt?" : "Zapisać zmiany produktu?", description: `${p._new ? "Utworzyć" : "Zapisać zmiany dla"} produkt „${p.sku.trim()}”?`, confirmText: "Zapisz" }))) return;
    const payload = { sku: p.sku.trim(), catalog_no: (p.catalog_no || "").trim(), type: p.type.trim() || "Inne", name: p.name.trim(), brand: (p.brand || "").trim() || "Inne", colors: split(p.colorsStr), sizes: split(p.sizesStr), branding_types: split(p.brandingStr), placements: split(p.placementsStr), gramatura: (p.gramatura || "").trim(), sklad: (p.sklad || "").trim(), kroj: (p.kroj || "").trim(), dekolt: (p.dekolt || "").trim(), szwy: (p.szwy || "").trim(), source: (p.source || "").trim(), ...(isAdmin ? { purchase_price: (p.purchase_price === "" || p.purchase_price == null) ? null : Number(p.purchase_price), wapro_index: Object.fromEntries(Object.entries(p.wapro_index || {}).filter(([k, v]) => split(p.sizesStr).includes(k) && (v ?? "").toString().trim() !== "").map(([k, v]) => [k, v.toString().trim()])), wapro_production_type: (p.wapro_production_type || "").trim(), wapro_input_index: (p.wapro_input_index || "").trim(), wapro_input_name: (p.wapro_input_name || "").trim(), wapro_output_index: (p.wapro_output_index || "").trim(), wapro_output_name: (p.wapro_output_name || "").trim() } : {}) };
    try {
      if (p._new) await api.post("/products", payload); else await api.put(`/products/${p.id}`, payload);
      toast.success(`Zapisano ${payload.sku}`);
      setEditId(null); await load(); onSaved && onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const cancel = (p) => { setEditId(null); if (p._new) setItems((prev) => prev.filter((x) => x.id !== p.id)); else load(); };

  const remove = async (p) => {
    if (p._new) { setEditId(null); return setItems((prev) => prev.filter((x) => x.id !== p.id)); }
    if (!(await confirm({ title: "Usunąć produkt?", description: `Czy na pewno usunąć produkt „${p.sku}”? Tej operacji nie można cofnąć.`, confirmText: "Usuń", danger: true }))) return;
    try { await api.delete(`/products/${p.id}`); toast.success("Usunięto"); await load(); onSaved && onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const q = query.trim().toLowerCase();
  const cf = colorFilter.trim().toLowerCase();
  const baseColors = useMemo(() => {
    const counts = {};
    items.forEach((p) => {
      const bases = new Set((p.colors || []).map(baseColorOf));
      bases.forEach((b) => { counts[b] = (counts[b] || 0) + 1; });
    });
    const order = [...COLOR_GROUPS.map(([l]) => l), "Inne"];
    return order.filter((l) => counts[l]).map((l) => ({ label: l, count: counts[l] }));
  }, [items]);
  const filtered = useMemo(() => items.filter((p) => {
    if (q && ![p.name, p.sku, p.catalog_no, p.type, p.brand, ...(p.colors || [])].join(" ").toLowerCase().includes(q)) return false;
    if (baseColor && !(p.colors || []).some((c) => baseColorOf(c) === baseColor)) return false;
    if (cf && !(p.colors || []).some((c) => c.toLowerCase().includes(cf))) return false;
    return true;
  }), [items, q, cf, baseColor]);

  const tree = useMemo(() => {
    const m = {};
    filtered.forEach((p) => {
      const b = p.brand || "Inne", t = p.type || "Inne";
      ((m[b] = m[b] || {})[t] = m[b][t] || []).push(p);
    });
    Object.values(m).forEach((types) => Object.values(types).forEach((arr) =>
      arr.sort((a, b) => (a.sku || "").localeCompare(b.sku || "", "pl", { numeric: true }))));
    return m;
  }, [filtered]);

  const editItem = items.find((p) => p.id === editId);
  const eB = editItem ? (editItem.brand || "Inne") : null;
  const eT = editItem ? (editItem.type || "Inne") : null;
  const brandOpen = (b) => !!q || open.has(`b:${b}`) || b === eB;
  const typeOpen = (b, t) => !!q || open.has(`t:${b}:${t}`) || (b === eB && t === eT);

  const editForm = (p) => (
    <div className="border-t border-border bg-secondary/30 p-4 space-y-3" data-testid={`product-edit-form-${p.id}`}>
      <div className="grid sm:grid-cols-5 gap-3">
        <div><Label className="text-xs font-semibold">Numer katalogowy</Label><Input data-testid={`product-catalogno-${p.id}`} value={p.catalog_no || ""} onChange={(e) => setField(p.id, "catalog_no", e.target.value)} className="mt-1 font-mono font-semibold" placeholder="np. 610.30" /></div>
        <div><Label className="text-xs font-semibold">Marka</Label><Input data-testid={`product-brand-${p.id}`} value={p.brand || ""} onChange={(e) => setField(p.id, "brand", e.target.value)} className="mt-1" placeholder="np. JHK" /></div>
        <div><Label className="text-xs font-semibold">Typ</Label><Input data-testid={`product-type-${p.id}`} value={p.type} onChange={(e) => setField(p.id, "type", e.target.value)} className="mt-1" placeholder="np. Koszulka" /></div>
        <div><Label className="text-xs font-semibold">SKU</Label><Input data-testid={`product-sku-${p.id}`} value={p.sku} onChange={(e) => setField(p.id, "sku", e.target.value)} className="mt-1 font-mono font-semibold" placeholder="np. TSRA190" /></div>
        <div><Label className="text-xs font-semibold">Nazwa (model)</Label><Input data-testid={`product-name-${p.id}`} value={p.name} onChange={(e) => setField(p.id, "name", e.target.value)} className="mt-1" placeholder="np. Premium T-shirt" /></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label className="text-xs font-semibold">Kolory (po przecinku)</Label><Input data-testid={`product-colors-${p.id}`} value={p.colorsStr} onChange={(e) => setField(p.id, "colorsStr", e.target.value)} className="mt-1 font-mono text-xs" placeholder="BLACK, WHITE, NAVY" /></div>
        <div><Label className="text-xs font-semibold">Rozmiary (po przecinku)</Label><Input data-testid={`product-sizes-${p.id}`} value={p.sizesStr} onChange={(e) => setField(p.id, "sizesStr", e.target.value)} className="mt-1 font-mono text-xs" placeholder="S, M, L, XL" /></div>
      </div>
      <div className="grid sm:grid-cols-5 gap-3">
        <div><Label className="text-xs font-semibold">Gramatura</Label><Input data-testid={`product-gramatura-${p.id}`} value={p.gramatura || ""} onChange={(e) => setField(p.id, "gramatura", e.target.value)} className="mt-1" placeholder="190 g/m²" /></div>
        <div><Label className="text-xs font-semibold">Skład</Label><Input data-testid={`product-sklad-${p.id}`} value={p.sklad || ""} onChange={(e) => setField(p.id, "sklad", e.target.value)} className="mt-1" placeholder="100% bawełna" /></div>
        <div><Label className="text-xs font-semibold">Krój</Label><Input data-testid={`product-kroj-${p.id}`} value={p.kroj || ""} onChange={(e) => setField(p.id, "kroj", e.target.value)} className="mt-1" placeholder="regular fit" /></div>
        <div><Label className="text-xs font-semibold">Dekolt</Label><Input data-testid={`product-dekolt-${p.id}`} value={p.dekolt || ""} onChange={(e) => setField(p.id, "dekolt", e.target.value)} className="mt-1" placeholder="okrągły, ściągacz" /></div>
        <div><Label className="text-xs font-semibold">Szwy boczne</Label><Input data-testid={`product-szwy-${p.id}`} value={p.szwy || ""} onChange={(e) => setField(p.id, "szwy", e.target.value)} className="mt-1" placeholder="brak" /></div>
      </div>
      {!p._new && isAdmin && (
        <div className="-mt-1 space-y-2">
          <div className="grid sm:grid-cols-[1fr_auto] gap-2 items-end">
            <div>
              <Label className="text-xs font-semibold">Źródło — adres karty producenta (opcjonalnie)</Label>
              <Input data-testid={`product-source-${p.id}`} value={p.source || ""} onChange={(e) => setField(p.id, "source", e.target.value)} className="mt-1 font-mono text-xs" placeholder="https://jhkpolska.pl/pl/... (wklej, gdy auto-dopasowanie nie trafia, np. torby/softshell)" />
            </div>
            <Button type="button" variant="outline" size="sm" data-testid={`product-fetch-specs-${p.id}`} disabled={fetching === p.id} onClick={() => fetchSpecs(p)} className="gap-2">
              {fetching === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Pobierz cechy
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">Bez adresu system sam wyszuka kartę (JHK/UTT). Dla toreb/softshell wklej link ręcznie, jeśli nie trafia.</p>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label className="text-xs font-semibold">Metody znakowania</Label><Input data-testid={`product-branding-${p.id}`} value={p.brandingStr} onChange={(e) => setField(p.id, "brandingStr", e.target.value)} className="mt-1 font-mono text-xs" placeholder={BR} /></div>
        <div><Label className="text-xs font-semibold">Miejsca znakowania</Label><Input data-testid={`product-placements-${p.id}`} value={p.placementsStr} onChange={(e) => setField(p.id, "placementsStr", e.target.value)} className="mt-1 font-mono text-xs" placeholder={PL} /></div>
      </div>
      {isAdmin && (
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs font-semibold flex items-center gap-1.5">Cena zakupu netto (zł) <span className="rounded bg-amber-400/15 px-1.5 py-0.5 text-[10px] font-bold text-amber-300">tylko admin</span></Label>
            <Input data-testid={`product-purchase-price-${p.id}`} type="number" step="0.01" min="0" value={p.purchase_price ?? ""} onChange={(e) => setField(p.id, "purchase_price", e.target.value)} className="mt-1" placeholder="np. 18.50" />
          </div>
        </div>
      )}
      {isAdmin && split(p.sizesStr).length > 0 && (
        <div className="rounded-xl border border-amber-400/30 bg-amber-400/5 p-3 space-y-2" data-testid={`product-wapro-${p.id}`}>
          <Label className="text-xs font-semibold flex items-center gap-1.5">Indeks katalogowy WAPRO <span className="rounded bg-amber-400/15 px-1.5 py-0.5 text-[10px] font-bold text-amber-300">per rozmiar · WAPRO MAG</span></Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {split(p.sizesStr).map((sz) => (
              <div key={sz}>
                <span className="text-[11px] text-muted-foreground font-mono">rozm. {sz}</span>
                <Input data-testid={`product-wapro-${p.id}-${sz}`} value={(p.wapro_index || {})[sz] ?? ""} onChange={(e) => setWapro(p.id, sz, e.target.value)} className="mt-0.5 font-mono text-xs" placeholder="np. 0352014060048" />
              </div>
            ))}
          </div>
        </div>
      )}
      {isAdmin && (
        <div className="rounded-xl border border-indigo-400/30 bg-indigo-400/5 p-3 space-y-3" data-testid={`product-wapro-config-${p.id}`}>
          <Label className="text-xs font-semibold flex items-center gap-1.5">Produkcja WAPRO <span className="rounded bg-indigo-400/15 px-1.5 py-0.5 text-[10px] font-bold text-indigo-300">ręczna konfiguracja</span></Label>
          <div>
            <span className="text-[11px] text-muted-foreground">Typ produkcji</span>
            <select data-testid={`product-wapro-type-${p.id}`} value={p.wapro_production_type || ""} onChange={(e) => setField(p.id, "wapro_production_type", e.target.value)} className="mt-0.5 w-full rounded-md border border-border bg-card px-2 py-2 text-sm">
              <option value="">— wybierz —</option>
              {["T-shirt", "Bluza", "Spodnie", "Softshell", "Czapka"].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            <div><span className="text-[11px] text-muted-foreground">Indeks DO PRODUKCJI</span><Input data-testid={`product-wapro-input-index-${p.id}`} value={p.wapro_input_index || ""} onChange={(e) => setField(p.id, "wapro_input_index", e.target.value)} className="mt-0.5 font-mono text-xs" placeholder="np. PZN000010" /></div>
            <div><span className="text-[11px] text-muted-foreground">Nazwa DO PRODUKCJI</span><Input data-testid={`product-wapro-input-name-${p.id}`} value={p.wapro_input_name || ""} onChange={(e) => setField(p.id, "wapro_input_name", e.target.value)} className="mt-0.5 text-xs" placeholder="np. Spodnie z logo klienta DO PRODUKCJI" /></div>
            <div><span className="text-[11px] text-muted-foreground">Indeks produktu końcowego</span><Input data-testid={`product-wapro-output-index-${p.id}`} value={p.wapro_output_index || ""} onChange={(e) => setField(p.id, "wapro_output_index", e.target.value)} className="mt-0.5 font-mono text-xs" placeholder="np. PZN000004" /></div>
            <div><span className="text-[11px] text-muted-foreground">Nazwa produktu końcowego</span><Input data-testid={`product-wapro-output-name-${p.id}`} value={p.wapro_output_name || ""} onChange={(e) => setField(p.id, "wapro_output_name", e.target.value)} className="mt-0.5 text-xs" placeholder="np. Spodnie z logo klienta" /></div>
          </div>
        </div>
      )}
      <div className="flex items-center gap-2">
        <Button data-testid={`product-save-${p.id}`} onClick={() => save(p)} size="sm" className="gap-2 bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold"><Save className="h-4 w-4" />Zapisz</Button>
        <Button data-testid={`product-cancel-${p.id}`} onClick={() => cancel(p)} size="sm" variant="outline" className="gap-2"><X className="h-4 w-4" />Anuluj</Button>
        <Button data-testid={`product-delete-${p.id}`} onClick={() => remove(p)} size="sm" variant="ghost" className="gap-2 text-rose-400 ml-auto"><Trash2 className="h-4 w-4" />Usuń</Button>
      </div>
    </div>
  );

  const row = (p) => (
    <div key={p.id} data-testid={`product-row-${p.sku || p.id}`} className="border-t border-border first:border-t-0">
      {editId === p.id ? editForm(p) : (
        <div className="flex items-center gap-3 px-4 py-2.5 hover:bg-accent/40">
          <Package className="h-4 w-4 text-muted-foreground shrink-0" />
          <span data-testid={`product-catalogno-badge-${p.sku || p.id}`} className={`font-mono text-xs font-bold shrink-0 w-24 truncate ${p.catalog_no ? "text-amber-300" : "text-muted-foreground/40"}`} title={p.catalog_no || ""}>{p.catalog_no || "—"}</span>
          <div className="flex-1 min-w-0">
            <span className="font-semibold truncate block">{p.name || "Nowy produkt"}</span>
            {(() => {
              const summary = commissionSummary(rules[p.sku]);
              return (
                <span data-testid={`product-commission-${p.sku || p.id}`} className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] font-medium">
                  {summary ? summary.map((s, i) => (
                    <span key={i} className={i === summary.length - 1 ? "text-muted-foreground" : "rounded bg-amber-400/15 px-1.5 py-0.5 font-mono font-bold text-amber-300"}>{s}</span>
                  )) : <span className="text-muted-foreground/50">Brak progów prowizji</span>}
                </span>
              );
            })()}
          </div>
          <span className="font-mono text-xs text-muted-foreground hidden sm:inline">{p.sku}</span>
          <span className="text-[11px] text-muted-foreground hidden md:inline">{(p.colors || []).length} kol. · {(p.sizes || []).length} rozm.</span>
          {isAdmin && p.purchase_price != null && <span data-testid={`product-cost-${p.sku || p.id}`} className="font-mono text-[11px] font-bold text-emerald-300 hidden lg:inline">zak. {Number(p.purchase_price).toFixed(2)} zł</span>}
          <Button data-testid={`product-editbtn-${p.sku || p.id}`} onClick={() => setEditId(p.id)} size="sm" variant="outline" className="gap-2 font-semibold"><Pencil className="h-3.5 w-3.5" />Edytuj</Button>
        </div>
      )}
    </div>
  );

  const brands = Object.keys(tree).sort((a, b) => (a === "Inne" ? 1 : b === "Inne" ? -1 : a.localeCompare(b)));

  return (
    <div data-testid="products-manager" className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <p className="font-mono text-xs font-bold uppercase tracking-widest text-amber-400">Produkty</p>
          <p className="text-sm font-medium text-muted-foreground">{items.length} modeli · pogrupowane wg marki i typu</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && <Button data-testid="product-profit-toggle" onClick={toggleProfit} variant="outline" className={`gap-2 font-bold uppercase tracking-wider ${showProfit ? "border-emerald-400/60 text-emerald-300 bg-emerald-400/10" : "border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10"}`}><TrendingUp className="h-4 w-4" />Zestawienie zysku</Button>}
          <Button data-testid="product-import" onClick={() => setImportOpen(true)} variant="outline" className="gap-2 border-amber-400/60 text-amber-300 hover:bg-amber-400/10 font-bold uppercase tracking-wider"><Link2 className="h-4 w-4" />Importuj ze strony</Button>
          <Button data-testid="product-add" onClick={addNew} className="gap-2 bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold uppercase tracking-wider"><Plus className="h-4 w-4" />Dodaj produkt</Button>
        </div>
      </div>

      <Dialog open={importOpen} onOpenChange={(o) => { if (!importing) setImportOpen(o); }}>
        <DialogContent data-testid="import-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide">Importuj produkt ze strony</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Wklej linki do stron produktów (np. JHK / jhkpolska.pl, CERVA, Milwaukee) — <b>każdy w nowej linii</b> (do 15 naraz). Jeden link = podgląd do sprawdzenia przed zapisem. Kilka linków = import wsadowy: nowe produkty dodają się od razu, istniejące (po SKU) są pomijane.</p>
            <Textarea data-testid="import-url-input" value={importUrl} onChange={(e) => setImportUrl(e.target.value)} placeholder={"https://jhkpolska.pl/pl/...\nhttps://jhkpolska.pl/pl/...\nhttps://www.cerva.com/pl/pl/..."} rows={5} className="font-mono text-xs" autoFocus />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)} disabled={importing}>Anuluj</Button>
            <Button data-testid="import-submit" onClick={runImport} disabled={importing || !importUrl.trim()} className="gap-2 bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold">
              {importing ? <><Loader2 className="h-4 w-4 animate-spin" />Pobieram…</> : <><Link2 className="h-4 w-4" />Importuj</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {isAdmin && showProfit && (
        <div data-testid="profit-panel" className="rounded-2xl border border-emerald-400/40 bg-card p-4 space-y-3">
          {!profit ? <div className="flex items-center gap-2 text-muted-foreground py-4"><Loader2 className="h-4 w-4 animate-spin" />Liczę zysk…</div> : (
            <>
              <div className="flex flex-wrap items-center gap-5">
                <div><p className="text-[11px] uppercase tracking-wider text-muted-foreground">Przychód</p><p className="font-display text-xl font-extrabold">{fmt(profit.totals?.revenue)} zł</p></div>
                <div><p className="text-[11px] uppercase tracking-wider text-muted-foreground">Koszt zakupu</p><p className="font-display text-xl font-extrabold">{fmt(profit.totals?.cost)} zł</p></div>
                <div><p className="text-[11px] uppercase tracking-wider text-muted-foreground">Zysk</p><p className="font-display text-xl font-extrabold text-emerald-400">{fmt(profit.totals?.profit)} zł</p></div>
                <p className="text-xs text-muted-foreground ml-auto max-w-xs">Zysk liczony tylko dla produktów z wpisaną ceną zakupu ({profit.totals?.with_cost || 0}). Uzupełnij ceny w edycji produktu.</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="py-1.5 pr-3">Produkt</th><th className="pr-3">Sprzedano</th><th className="pr-3">Śr. cena</th><th className="pr-3">Cena zakupu</th><th className="pr-3">Zysk</th><th>Marża</th></tr></thead>
                  <tbody>
                    {(profit.rows || []).filter((r) => r.qty > 0 || r.purchase_price != null).slice(0, 100).map((r) => (
                      <tr key={r.sku} data-testid={`profit-row-${r.sku}`} className="border-b border-border/50">
                        <td className="py-1.5 pr-3"><span className="font-semibold">{r.name}</span> <span className="font-mono text-xs text-muted-foreground">{r.sku}</span></td>
                        <td className="pr-3 whitespace-nowrap">{r.qty} szt.</td>
                        <td className="pr-3 whitespace-nowrap">{r.avg_price != null ? `${fmt(r.avg_price)} zł` : "—"}</td>
                        <td className="pr-3 whitespace-nowrap">{r.purchase_price != null ? `${fmt(r.purchase_price)} zł` : <span className="text-muted-foreground/40">brak</span>}</td>
                        <td className={`pr-3 whitespace-nowrap font-bold ${r.profit != null ? (r.profit >= 0 ? "text-emerald-400" : "text-rose-400") : ""}`}>{r.profit != null ? `${fmt(r.profit)} zł` : "—"}</td>
                        <td className="whitespace-nowrap">{r.margin_pct != null ? `${r.margin_pct}%` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {baseColors.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="base-color-chips">
          <button
            data-testid="base-color-all"
            onClick={() => { setBaseColor(""); setColorFilter(""); }}
            className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider transition-colors ${!baseColor && !cf ? "bg-amber-400 text-[#0D1117]" : "bg-secondary text-muted-foreground hover:bg-secondary/70"}`}
          >Wszystkie</button>
          {baseColors.map(({ label, count }) => (
            <button
              key={label}
              data-testid={`base-color-${label}`}
              onClick={() => { setBaseColor(baseColor === label ? "" : label); setColorFilter(""); }}
              className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider transition-colors ${baseColor === label ? "bg-amber-400 text-[#0D1117]" : "bg-secondary text-muted-foreground hover:bg-secondary/70"}`}
            >{label} <span className="opacity-70">{count}</span></button>
          ))}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="products-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Szukaj po numerze katalogowym, nazwie, SKU, marce, kolorze…" className="pl-9" />
        </div>
        <div className="relative w-full sm:w-64">
          <Input data-testid="products-color-filter" value={colorFilter} onChange={(e) => { setColorFilter(e.target.value); setBaseColor(""); }} placeholder="Filtr koloru — wpisz tekst…" className="pr-8" />
          {colorFilter && <button data-testid="products-color-clear" onClick={() => setColorFilter("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>}
        </div>
      </div>

      {(baseColor || cf) && <p className="text-xs font-medium text-muted-foreground" data-testid="color-filter-info">{baseColor ? `Kolor bazowy „${baseColor}" (wszystkie odcienie)` : `Kolor zawierający „${colorFilter}"`} — {filtered.length} modeli.</p>}

      {brands.length === 0 && <div className="rounded-2xl border border-dashed border-border p-12 text-center font-semibold text-muted-foreground">Brak produktów.</div>}

      <div className="space-y-3">
        {brands.map((b) => {
          const types = tree[b];
          const bCount = Object.values(types).reduce((a, arr) => a + arr.length, 0);
          const bOpen = brandOpen(b);
          return (
            <div key={b} className="rounded-2xl border border-border bg-card overflow-hidden">
              <button data-testid={`brand-group-${b}`} onClick={() => toggle(`b:${b}`)} className="flex w-full items-center gap-3 px-4 py-3 bg-secondary/50 hover:bg-secondary transition-colors">
                {bOpen ? <ChevronDown className="h-4 w-4 text-amber-400" /> : <ChevronRight className="h-4 w-4 text-amber-400" />}
                <span className="font-display text-lg font-extrabold">{b}</span>
                <span className="rounded-md bg-amber-400/15 px-2 py-0.5 text-[11px] font-bold text-amber-300">{bCount}</span>
              </button>
              {bOpen && (
                <div className="divide-y divide-border">
                  {Object.keys(types).sort((x, y) => (x === "Inne" ? 1 : y === "Inne" ? -1 : x.localeCompare(y))).map((t) => {
                    const prods = types[t];
                    const tOpen = typeOpen(b, t);
                    return (
                      <div key={t}>
                        <button data-testid={`type-group-${b}-${t}`} onClick={() => toggle(`t:${b}:${t}`)} className="flex w-full items-center gap-2.5 px-4 py-2 pl-6 hover:bg-accent/40 transition-colors">
                          {tOpen ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                          <span className="font-semibold text-sm">{t}</span>
                          <span className="text-[11px] font-mono text-muted-foreground">({prods.length})</span>
                        </button>
                        {tOpen && <div className="pl-4 bg-background/40">{prods.map(row)}</div>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
